import { Bell, Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { reminderFrequencies, weekDays, monthDays } from "@/data/reminders";
import { useReminderSettings } from "@/hooks/useReminderSettings";

const FieldLabel = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="text-sm font-medium text-[#1A3626]">
    {children}
  </label>
);

export const ReminderSettingsPage = () => {
  const { error, loading, saveSettings, saving, settings, updateField } = useReminderSettings();

  return (
    <main className="devotional-shell min-h-screen px-4 py-8 sm:px-6 lg:px-8" data-testid="reminder-settings-page">
      <form onSubmit={saveSettings} className="mx-auto max-w-4xl space-y-8" data-testid="reminder-settings-form">
        <section className="gentle-reveal" data-testid="reminder-settings-header">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8C9B90]" data-testid="reminder-settings-overline">Reflection reminders</p>
          <h1 className="mt-3 font-serif-display text-4xl font-semibold leading-tight text-[#1A3626] sm:text-5xl lg:text-6xl" data-testid="reminder-settings-heading">Choose when Fruitful should prompt you.</h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#4A5D4E]" data-testid="reminder-settings-copy">Turn on an in-app reminder and choose a daily, weekly, or monthly rhythm. The reminder appears on your dashboard when it is time to reflect.</p>
        </section>

        <section className="rounded-2xl border border-[#E5E1D8] bg-white p-5 shadow-[0_8px_30px_rgba(26,54,38,0.04)] sm:p-7" data-testid="reminder-settings-card">
          {loading ? (
            <div className="flex min-h-48 items-center justify-center font-serif-display text-3xl text-[#1A3626]" data-testid="reminder-settings-loading">Loading reminder settings…</div>
          ) : (
            <div className="space-y-7">
              <div className="flex flex-col gap-4 rounded-xl bg-[#F9F6F0] p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="reminder-enabled-row">
                <div>
                  <h2 className="font-serif-display text-2xl font-semibold text-[#1A3626]" data-testid="reminder-enabled-title">In-app reminder banner</h2>
                  <p className="text-sm text-[#4A5D4E]" data-testid="reminder-enabled-copy">Show a reminder on the dashboard when your reflection is due.</p>
                </div>
                <Switch checked={settings.enabled} onCheckedChange={(value) => updateField("enabled", value)} data-testid="reminder-enabled-switch" />
              </div>

              <div className="grid gap-5 md:grid-cols-2" data-testid="reminder-settings-fields">
                <div className="space-y-2" data-testid="reminder-frequency-field">
                  <FieldLabel htmlFor="reminder-frequency">Frequency</FieldLabel>
                  <select id="reminder-frequency" value={settings.frequency} onChange={(event) => updateField("frequency", event.target.value)} className="h-12 w-full rounded-xl border border-[#E5E1D8] bg-[#F9F6F0] px-3 text-[#1A3626] outline-none focus:ring-2 focus:ring-[#C87961]" data-testid="reminder-frequency-select">
                    {reminderFrequencies.map((frequency) => <option key={frequency.value} value={frequency.value}>{frequency.label}</option>)}
                  </select>
                </div>

                <div className="space-y-2" data-testid="reminder-time-field">
                  <FieldLabel htmlFor="reminder-time">Preferred time</FieldLabel>
                  <input id="reminder-time" type="time" value={settings.preferred_time} onChange={(event) => updateField("preferred_time", event.target.value)} className="h-12 w-full rounded-xl border border-[#E5E1D8] bg-[#F9F6F0] px-3 text-[#1A3626] outline-none focus:ring-2 focus:ring-[#C87961]" data-testid="reminder-time-input" />
                </div>

                {settings.frequency === "weekly" && (
                  <div className="space-y-2" data-testid="reminder-weekday-field">
                    <FieldLabel htmlFor="reminder-weekday">Preferred day</FieldLabel>
                    <select id="reminder-weekday" value={settings.weekly_day} onChange={(event) => updateField("weekly_day", Number(event.target.value))} className="h-12 w-full rounded-xl border border-[#E5E1D8] bg-[#F9F6F0] px-3 text-[#1A3626] outline-none focus:ring-2 focus:ring-[#C87961]" data-testid="reminder-weekday-select">
                      {weekDays.map((day) => <option key={day.value} value={day.value}>{day.label}</option>)}
                    </select>
                  </div>
                )}

                {settings.frequency === "monthly" && (
                  <div className="space-y-2" data-testid="reminder-monthday-field">
                    <FieldLabel htmlFor="reminder-monthday">Preferred day of month</FieldLabel>
                    <select id="reminder-monthday" value={settings.monthly_day} onChange={(event) => updateField("monthly_day", Number(event.target.value))} className="h-12 w-full rounded-xl border border-[#E5E1D8] bg-[#F9F6F0] px-3 text-[#1A3626] outline-none focus:ring-2 focus:ring-[#C87961]" data-testid="reminder-monthday-select">
                      {monthDays.map((day) => <option key={day.value} value={day.value}>{day.label}</option>)}
                    </select>
                  </div>
                )}
              </div>

              {error && <p className="rounded-xl bg-[#FFF4F1] p-4 text-sm text-[#A85F4B]" data-testid="reminder-settings-error">{error}</p>}

              <div className="flex justify-end" data-testid="reminder-save-row">
                <Button type="submit" disabled={saving} className="h-12 rounded-full bg-[#1A3626] px-7 text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#4A5D4E]" data-testid="reminder-save-button">
                  {saving ? <Loader2 className="animate-spin" size={17} /> : <Save size={17} />}
                  Save reminder
                </Button>
              </div>
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-[#E5E1D8] bg-[#1A3626] p-5 text-white sm:p-7" data-testid="reminder-future-note">
          <div className="flex gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/12" data-testid="reminder-future-icon"><Bell size={20} /></span>
            <div>
              <h2 className="font-serif-display text-2xl font-semibold" data-testid="reminder-future-title">More reminder types can come later</h2>
              <p className="mt-1 text-sm leading-relaxed text-white/78" data-testid="reminder-future-copy">This setting starts with in-app dashboard reminders. Browser notifications or email reminders can be added in a future version.</p>
            </div>
          </div>
        </section>
      </form>
    </main>
  );
};